import { Html } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { axialToWorld, getHexTileMapKey } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import type { RuleSet } from '@/game/rules';
import type { Building, PlayerId, SimState } from '@/game/sim';
import { constructionLabel, constructionStatus, formatDuration } from '@/game/view/construction';
import { upgradeProgress } from '@/game/view/upgradeProgress';
import { useSimFrame } from './app/simFrame';

/** Keep in-world labels under the HUD, notifications and end screen (z-10+). */
export const HTML_Z_RANGE: [number, number] = [5, 0];

const LABEL_HEIGHT = 1.35;
/** Labels hide when the camera is higher than this (zoomed far out), where they'd pile up. */
const HIDE_ABOVE_HEIGHT = 45;
/** Extra height for labels on odd columns. */
const STAGGER = 0.45;

/**
 * A pill over an own building whose upgrade is under way: a progress ring and
 * the seconds left ("⬆ 31s"; "⏸ 12s" in the warn colour while held under
 * attack). Own only: a rival's upgrade is a secret (`upgradeProgress`).
 */
function upgradeLabel(state: SimState, rules: RuleSet, b: Building, playerId: PlayerId) {
  const up = upgradeProgress(state, rules, b, playerId);
  if (!up) return null;
  const world = axialToWorld(b.pos, config.hexMap.tile.radius);
  const color = up.paused ? 'var(--color-warn)' : 'var(--color-accent)';
  return (
    <Html
      key={b.id}
      position={[world.x, LABEL_HEIGHT + (Math.abs(b.pos.q) % 2) * STAGGER, world.z]}
      center
      zIndexRange={HTML_Z_RANGE}
      style={{ pointerEvents: 'none' }}
    >
      <div
        className={`pill inline-flex items-center gap-1.5 py-0.5 pr-2 pl-1 whitespace-nowrap text-[11px] tabular-nums ${up.paused ? 'text-warn' : ''}`}
      >
        <span
          aria-hidden="true"
          className="grid size-3.5 place-items-center rounded-full after:size-2 after:rounded-full after:bg-surface-strong"
          style={{
            background: `conic-gradient(${color} ${up.fraction * 100}%, var(--color-fill) 0)`,
          }}
        />
        <span>
          {up.paused ? '⏸' : '⬆'} {formatDuration(up.secondsLeft)}
        </span>
      </div>
    </Html>
  );
}

/**
 * Floating progress bar + time left above every unfinished building the
 * viewer can see (own always; rivals' only while the tile is in sight).
 */
export function ConstructionLabels() {
  const camera = useThree((s) => s.camera);
  const { view: state, playerId, rules, fog } = useSimFrame();
  const getTileVisibility = fog.visibility;
  // Re-rendered every tick, so this follows zoom closely enough.
  if (camera.position.y > HIDE_ABOVE_HEIGHT) return null;
  return (
    <>
      {Object.values(state.buildings).map((b) => {
        if (b.owner !== playerId && getTileVisibility(getHexTileMapKey(b.pos)) !== 'visible') {
          return null;
        }
        const status = constructionStatus(state, rules, b);
        if (!status) return upgradeLabel(state, rules, b, playerId);
        const world = axialToWorld(b.pos, config.hexMap.tile.radius);
        return (
          <Html
            key={b.id}
            // Side-by-side sites (same row) alternate heights so their labels don't overlap.
            position={[world.x, LABEL_HEIGHT + (Math.abs(b.pos.q) % 2) * STAGGER, world.z]}
            center
            zIndexRange={HTML_Z_RANGE}
            style={{ pointerEvents: 'none' }}
          >
            <div className="panel rounded-lg bg-surface-strong px-1.5 py-1 text-center text-[10px] leading-tight">
              <div className="h-1 w-full overflow-hidden rounded-full bg-fill">
                <div
                  className="h-full rounded-full bg-accent"
                  style={{ width: `${status.progress * 100}%` }}
                />
              </div>
              <div className="mt-0.5 whitespace-nowrap">
                {constructionLabel(rules.buildings[b.type]?.name ?? b.type, status)}
              </div>
            </div>
          </Html>
        );
      })}
    </>
  );
}
